import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, Edit3Icon, ShieldCheckIcon, BuildingIcon, CreditCardIcon, CalendarIcon, SearchIcon } from 'lucide-react';
import { InvoiceDetails, InvoiceDocument } from '../types/invoice';
import { Tabs } from './ui/Tabs';
import { Select } from './ui/Select';
import { Input } from './ui/Input';
import { VendorSelector } from './VendorSelector';
import { formatCurrency, formatAmount } from '../utils/currencyFormat';
import { toInputDate, fromInputDate } from '../utils/dateFormat';
import { getVendorDetails, findVendorByName } from '../utils/vendorMapping';
import { POHeaderSearchModal } from './POHeaderSearchModal';
interface DocumentDataPanelProps {
  details: InvoiceDetails;
  document: InvoiceDocument;
  isEditAllMode?: boolean;
  onUpdateDocument?: (updates: Partial<InvoiceDocument>) => void;
  isViewerDetached?: boolean;
  validation?: ValidationResult;
}
export function DocumentDataPanel({
  details,
  document,
  isEditAllMode = false,
  onUpdateDocument,
  isViewerDetached = false,
  validation
}: DocumentDataPanelProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [isEditingVendor, setIsEditingVendor] = useState(false);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [showPOSearchModal, setShowPOSearchModal] = useState(false);
  // Initialize selectedVendorId from current document supplier
  useEffect(() => {
    const currentVendor = findVendorByName(document.supplier);
    if (currentVendor) {
      setSelectedVendorId(currentVendor.id);
    }
  }, [document.supplier]);
  // Sync with Edit All mode
  useEffect(() => {
    if (isEditAllMode) {
      setIsEditingVendor(true);
      setIsEditingHeader(true);
    } else {
      setIsEditingVendor(false);
      setIsEditingHeader(false);
    }
  }, [isEditAllMode]);
  // Editable document header state - NOW INCLUDING AMOUNTS
  const [editableHeader, setEditableHeader] = useState({
    invoiceNumber: document.invoiceNumber,
    invoiceDate: toInputDate(document.invoiceDate),
    dueDate: toInputDate(document.dueDate),
    currency: document.currency,
    poNumber: document.poNumber || '',
    subtotal: document.subtotal.toFixed(2),
    taxAmt: document.taxAmt.toFixed(2),
    total: document.total.toFixed(2)
  });
  // Sync editableHeader when document changes
  useEffect(() => {
    setEditableHeader({
      invoiceNumber: document.invoiceNumber,
      invoiceDate: toInputDate(document.invoiceDate),
      dueDate: toInputDate(document.dueDate),
      currency: document.currency,
      poNumber: document.poNumber || '',
      subtotal: document.subtotal.toFixed(2),
      taxAmt: document.taxAmt.toFixed(2),
      total: document.total.toFixed(2)
    });
  }, [document]);
  const tabs = [{
    id: 'details',
    label: 'Details'
  }, {
    id: 'notes',
    label: 'Notes'
  }, {
    id: 'attachments',
    label: 'Attachments'
  }, {
    id: 'verification',
    label: 'Vendor Verification'
  }];
  const handleSaveHeader = () => {
    if (!onUpdateDocument) return;
    // Convert string values to numbers before saving
    const updates = {
      invoiceNumber: editableHeader.invoiceNumber,
      invoiceDate: fromInputDate(editableHeader.invoiceDate),
      dueDate: fromInputDate(editableHeader.dueDate),
      currency: editableHeader.currency,
      poNumber: editableHeader.poNumber,
      subtotal: parseFloat(editableHeader.subtotal) || 0,
      taxAmt: parseFloat(editableHeader.taxAmt) || 0,
      total: parseFloat(editableHeader.total) || 0
    };
    onUpdateDocument(updates);
    if (!isEditAllMode) {
      setIsEditingHeader(false);
    }
  };
  const handleCancelHeader = () => {
    setEditableHeader({
      invoiceNumber: document.invoiceNumber,
      invoiceDate: toInputDate(document.invoiceDate),
      dueDate: toInputDate(document.dueDate),
      currency: document.currency,
      poNumber: document.poNumber || '',
      subtotal: document.subtotal.toFixed(2),
      taxAmt: document.taxAmt.toFixed(2),
      total: document.total.toFixed(2)
    });
    if (!isEditAllMode) {
      setIsEditingHeader(false);
    }
  };
  const handleSaveVendor = () => {
    if (selectedVendorId && onUpdateDocument) {
      // Get vendor details and update document
      const vendorDetails = getVendorDetails(selectedVendorId);
      if (vendorDetails) {
        onUpdateDocument({
          supplier: vendorDetails.supplier,
          supplierABN: vendorDetails.supplierABN
        });
      }
    }
    if (!isEditAllMode) {
      setIsEditingVendor(false);
    }
  };
  const handleCancelVendor = () => {
    // Reset to current document's vendor
    const currentVendor = findVendorByName(document.supplier);
    if (currentVendor) {
      setSelectedVendorId(currentVendor.id);
    } else {
      setSelectedVendorId(null);
    }
    if (!isEditAllMode) {
      setIsEditingVendor(false);
    }
  };
  const handlePOSelect = (poNumber: string, matchingType: '2-way' | '3-way') => {
    if (!onUpdateDocument) return;
    // Update document with PO number and matching type
    onUpdateDocument({
      poNumber,
      matchingType
    });
    // Update editable header state
    setEditableHeader({
      ...editableHeader,
      poNumber
    });
    // Close the modal
    setShowPOSearchModal(false);
    // Automatically save the header changes
    if (!isEditAllMode) {
      setIsEditingHeader(false);
    }
  };
  return <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-full" data-id="element-93">
        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200 flex-shrink-0" data-id="element-94">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-95" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0" data-id="element-96">
          {activeTab === 'details' && <div className="space-y-4" data-id="element-97">
              {/* Vendor Name with Edit Button */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-98">
                <div className="flex-1" data-id="element-99">
                  {isEditingVendor ? <div className="space-y-2" data-id="element-100">
                      <label className="block text-xs font-medium text-gray-700" data-id="element-101">
                        Vendor
                      </label>
                      <div className="flex items-center gap-2" data-id="element-102">
                        <div className="flex-1" data-id="element-103">
                          <VendorSelector value={selectedVendorId} onChange={setSelectedVendorId} currentEntityId="entity-1" currentSiteId="site-1" placeholder="Search and select vendor..." data-id="element-104" />
                        </div>
                        {!isEditAllMode && <>
                            <button onClick={handleSaveVendor} className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors" data-id="element-105">
                              Save
                            </button>
                            <button onClick={handleCancelVendor} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-106">
                              Cancel
                            </button>
                          </>}
                      </div>
                    </div> : <div className="flex items-center gap-3" data-id="element-107">
                      <div data-id="element-108">
                        <div className="text-xs text-gray-500 mb-0.5" data-id="element-109">
                          Vendor
                        </div>
                        <div className="text-lg font-semibold text-gray-900" data-id="element-110">
                          {details.vendorName}
                        </div>
                      </div>
                    </div>}
                </div>
                {!isEditingVendor && !isEditAllMode && <button onClick={() => setIsEditingVendor(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" title="Edit vendor" data-id="element-111">
                    <Edit3Icon className="w-3.5 h-3.5" data-id="element-112" />
                    Edit
                  </button>}
              </div>

              {/* Vendor Details Grid - 3 Column Layout */}
              <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-113">
                {/* Column 1: Vendor Information */}
                <div className="space-y-3" data-id="element-114">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-115">
                    Vendor
                  </div>
                  <div data-id="element-116">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-117">
                      Vendor Name:
                    </label>
                    <div className="text-sm" data-id="element-118">{details.vendorName}</div>
                  </div>
                  <div data-id="element-119">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-120">
                      Supplier Code:
                    </label>
                    <div className="text-sm" data-id="element-121">{details.supplierCode}</div>
                  </div>
                  <div data-id="element-122">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-123">
                      Vendor Tax Number:
                    </label>
                    <div className="text-sm" data-id="element-124">{details.vendorTaxNo}</div>
                  </div>
                  <div data-id="element-125">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-126">
                      Email:
                    </label>
                    <div className="text-sm" data-id="element-127">{details.email}</div>
                  </div>
                  <div data-id="element-128">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-129">
                      Phone Number:
                    </label>
                    <div className="text-sm" data-id="element-130">{details.phoneNumber || '-'}</div>
                  </div>
                  <div data-id="element-131">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-132">
                      Vendor Bank Check:
                    </label>
                    <div className="flex items-center" data-id="element-133">
                      {details.vendorBankCheck && <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-134" />}
                    </div>
                  </div>
                </div>

                {/* Column 2: Entity Information */}
                <div className="space-y-3" data-id="element-135">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-136">
                    Entity
                  </div>
                  <div data-id="element-137">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-138">
                      Entity:
                    </label>
                    <div className="text-sm" data-id="element-139">{details.entity}</div>
                  </div>
                </div>

                {/* Column 3: Site & Address Information */}
                <div className="space-y-3" data-id="element-140">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-141">
                    Site & Address
                  </div>
                  <div data-id="element-142">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-143">
                      Site:
                    </label>
                    <div className="text-sm" data-id="element-144">{details.site}</div>
                  </div>
                  <div data-id="element-145">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-146">
                      Address:
                    </label>
                    <div className="text-sm" data-id="element-147">{details.address}</div>
                  </div>
                  <div data-id="element-148">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-149">
                      City:
                    </label>
                    <div className="text-sm" data-id="element-150">{details.city}</div>
                  </div>
                  <div data-id="element-151">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-152">
                      State:
                    </label>
                    <div className="text-sm" data-id="element-153">{details.state}</div>
                  </div>
                  <div data-id="element-154">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-155">
                      Postcode:
                    </label>
                    <div className="text-sm" data-id="element-156">{details.postcode}</div>
                  </div>
                  <div data-id="element-157">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-158">
                      Country:
                    </label>
                    <div className="text-sm" data-id="element-159">{details.country}</div>
                  </div>
                </div>
              </div>

              {/* Document Header */}
              <div className="pt-4 border-t border-gray-200" data-id="element-160">
                <div className="flex items-center justify-between mb-3" data-id="element-161">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-162">
                    Document Header
                  </h3>
                  {isEditingHeader ? !isEditAllMode && <div className="flex items-center gap-2" data-id="element-163">
                          <button onClick={handleSaveHeader} className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors" data-id="element-164">
                            Save
                          </button>
                          <button onClick={handleCancelHeader} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-165">
                            Cancel
                          </button>
                        </div> : !isEditAllMode && <button onClick={() => setIsEditingHeader(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" title="Edit document header" data-id="element-166">
                          <Edit3Icon className="w-3.5 h-3.5" data-id="element-167" />
                          Edit
                        </button>}
                </div>

                {isEditingHeader ? <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-168">
                    {/* Column 1: Document Information */}
                    <div className="space-y-3" data-id="element-169">
                      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-170">
                        Document
                      </div>
                      <div data-id="element-171">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-172">
                          Document Number: *
                        </label>
                        <Input value={editableHeader.invoiceNumber} onChange={e => setEditableHeader({
                    ...editableHeader,
                    invoiceNumber: e.target.value
                  })} className="w-full" data-id="element-173" />
                      </div>
                      <div data-id="element-174">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-175">
                          Currency: *
                        </label>
                        <Input value={editableHeader.currency} onChange={e => setEditableHeader({
                    ...editableHeader,
                    currency: e.target.value
                  })} className="w-full" data-id="element-176" />
                      </div>
                      <div data-id="element-177">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-178">
                          PO Number:
                        </label>
                        <div className="relative" data-id="element-179">
                          <Input value={editableHeader.poNumber} onChange={e => setEditableHeader({
                      ...editableHeader,
                      poNumber: e.target.value
                    })} placeholder="Optional" className="w-full pr-10" data-id="element-180" />
                          <button type="button" onClick={() => setShowPOSearchModal(true)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded transition-colors" title="Search for PO" data-id="element-181">
                            <SearchIcon className="w-4 h-4 text-gray-600" data-id="element-182" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Dates */}
                    <div className="space-y-3" data-id="element-183">
                      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-184">
                        Dates
                      </div>
                      <div data-id="element-185">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-186">
                          Document Date:
                        </label>
                        <div className="text-sm" data-id="element-187">{document.invoiceDate}</div>
                      </div>
                      <div data-id="element-188">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-189">
                          Due Date:
                        </label>
                        <div className="text-sm" data-id="element-190">{document.dueDate}</div>
                      </div>
                      {document.receivedDate && <div data-id="element-191">
                          <label className="block text-xs text-gray-600 mb-0.5" data-id="element-192">
                            Received Date:
                          </label>
                          <div className="text-sm" data-id="element-193">{document.receivedDate}</div>
                        </div>}
                    </div>

                    {/* Column 3: Amounts */}
                    <div className="space-y-3" data-id="element-194">
                      <div className={`${validation?.errors.some(e => e.field === 'subtotal') ? 'border-2 border-red-500 rounded px-2 py-1 bg-red-50' : ''}`} data-id="element-195">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-196">
                          Subtotal:
                        </label>
                        <div className="relative group" data-id="element-197">
                          <div className="text-sm" data-id="element-198">
                            {formatAmount(document.subtotal, document.currency)}
                          </div>
                          {validation?.errors.some(e => e.field === 'subtotal') && <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64" data-id="element-199">
                              <div className="bg-red-900 text-white text-xs rounded-lg shadow-lg p-3" data-id="element-200">
                                <div className="font-semibold mb-1" data-id="element-201">
                                  {validation.errors.find(e => e.field === 'subtotal')?.rule}
                                </div>
                                <div data-id="element-202">
                                  {validation.errors.find(e => e.field === 'subtotal')?.message}
                                </div>
                                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-900" data-id="element-203"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                      <div className={`${validation?.errors.some(e => e.field === 'taxAmt') ? 'border-2 border-red-500 rounded px-2 py-1 bg-red-50' : ''}`} data-id="element-204">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-205">
                          Tax:
                        </label>
                        <div className="relative group" data-id="element-206">
                          <div className="text-sm" data-id="element-207">
                            {formatAmount(document.taxAmt, document.currency)}
                          </div>
                          {validation?.errors.some(e => e.field === 'taxAmt') && <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64" data-id="element-208">
                              <div className="bg-red-900 text-white text-xs rounded-lg shadow-lg p-3" data-id="element-209">
                                <div className="font-semibold mb-1" data-id="element-210">
                                  {validation.errors.find(e => e.field === 'taxAmt')?.rule}
                                </div>
                                <div data-id="element-211">
                                  {validation.errors.find(e => e.field === 'taxAmt')?.message}
                                </div>
                                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-900" data-id="element-212"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                      <div className={`${validation?.errors.some(e => e.field === 'total') ? 'border-2 border-red-500 rounded px-2 py-1 bg-red-50' : ''}`} data-id="element-213">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-214">
                          Total:
                        </label>
                        <div className="relative group" data-id="element-215">
                          <div className="text-sm font-medium" data-id="element-216">
                            {formatAmount(document.total, document.currency)}
                          </div>
                          {validation?.errors.some(e => e.field === 'total') && <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64" data-id="element-217">
                              <div className="bg-red-900 text-white text-xs rounded-lg shadow-lg p-3" data-id="element-218">
                                <div className="font-semibold mb-1" data-id="element-219">
                                  {validation.errors.find(e => e.field === 'total')?.rule}
                                </div>
                                <div data-id="element-220">
                                  {validation.errors.find(e => e.field === 'total')?.message}
                                </div>
                                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-900" data-id="element-221"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </div>
                  </div> : <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-222">
                    {/* Column 1: Document Information */}
                    <div className="space-y-3" data-id="element-223">
                      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-224">
                        Document
                      </div>
                      <div data-id="element-225">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-226">
                          Document Number:
                        </label>
                        <div className="text-sm" data-id="element-227">{document.invoiceNumber}</div>
                      </div>
                      <div data-id="element-228">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-229">
                          Currency:
                        </label>
                        <div className="text-sm" data-id="element-230">{document.currency}</div>
                      </div>
                      {document.poNumber && <div data-id="element-231">
                          <label className="block text-xs text-gray-600 mb-0.5" data-id="element-232">
                            PO Number:
                          </label>
                          <div className="text-sm font-medium text-blue-600" data-id="element-233">
                            {document.poNumber}
                          </div>
                        </div>}
                    </div>

                    {/* Column 2: Dates */}
                    <div className="space-y-3" data-id="element-234">
                      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-235">
                        Dates
                      </div>
                      <div data-id="element-236">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-237">
                          Document Date:
                        </label>
                        <div className="text-sm" data-id="element-238">{document.invoiceDate}</div>
                      </div>
                      <div data-id="element-239">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-240">
                          Due Date:
                        </label>
                        <div className="text-sm" data-id="element-241">{document.dueDate}</div>
                      </div>
                      {document.receivedDate && <div data-id="element-242">
                          <label className="block text-xs text-gray-600 mb-0.5" data-id="element-243">
                            Received Date:
                          </label>
                          <div className="text-sm" data-id="element-244">{document.receivedDate}</div>
                        </div>}
                    </div>

                    {/* Column 3: Amounts */}
                    <div className="space-y-3" data-id="element-245">
                      <div className={`${validation?.errors.some(e => e.field === 'subtotal') ? 'border-2 border-red-500 rounded px-2 py-1 bg-red-50' : ''}`} data-id="element-246">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-247">
                          Subtotal:
                        </label>
                        <div className="relative group" data-id="element-248">
                          <div className="text-sm" data-id="element-249">
                            {formatAmount(document.subtotal, document.currency)}
                          </div>
                          {validation?.errors.some(e => e.field === 'subtotal') && <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64" data-id="element-250">
                              <div className="bg-red-900 text-white text-xs rounded-lg shadow-lg p-3" data-id="element-251">
                                <div className="font-semibold mb-1" data-id="element-252">
                                  {validation.errors.find(e => e.field === 'subtotal')?.rule}
                                </div>
                                <div data-id="element-253">
                                  {validation.errors.find(e => e.field === 'subtotal')?.message}
                                </div>
                                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-900" data-id="element-254"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                      <div className={`${validation?.errors.some(e => e.field === 'taxAmt') ? 'border-2 border-red-500 rounded px-2 py-1 bg-red-50' : ''}`} data-id="element-255">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-256">
                          Tax:
                        </label>
                        <div className="relative group" data-id="element-257">
                          <div className="text-sm" data-id="element-258">
                            {formatAmount(document.taxAmt, document.currency)}
                          </div>
                          {validation?.errors.some(e => e.field === 'taxAmt') && <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64" data-id="element-259">
                              <div className="bg-red-900 text-white text-xs rounded-lg shadow-lg p-3" data-id="element-260">
                                <div className="font-semibold mb-1" data-id="element-261">
                                  {validation.errors.find(e => e.field === 'taxAmt')?.rule}
                                </div>
                                <div data-id="element-262">
                                  {validation.errors.find(e => e.field === 'taxAmt')?.message}
                                </div>
                                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-900" data-id="element-263"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                      <div className={`${validation?.errors.some(e => e.field === 'total') ? 'border-2 border-red-500 rounded px-2 py-1 bg-red-50' : ''}`} data-id="element-264">
                        <label className="block text-xs text-gray-600 mb-0.5" data-id="element-265">
                          Total:
                        </label>
                        <div className="relative group" data-id="element-266">
                          <div className="text-sm font-medium" data-id="element-267">
                            {formatAmount(document.total, document.currency)}
                          </div>
                          {validation?.errors.some(e => e.field === 'total') && <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64" data-id="element-268">
                              <div className="bg-red-900 text-white text-xs rounded-lg shadow-lg p-3" data-id="element-269">
                                <div className="font-semibold mb-1" data-id="element-270">
                                  {validation.errors.find(e => e.field === 'total')?.rule}
                                </div>
                                <div data-id="element-271">
                                  {validation.errors.find(e => e.field === 'total')?.message}
                                </div>
                                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-900" data-id="element-272"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>}

          {activeTab === 'notes' && <div className="space-y-4" data-id="element-273">
              <div data-id="element-274">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-275">
                  Invoice Notes
                </label>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded border border-gray-200" data-id="element-276">
                  {document.notes || 'No notes available'}
                </div>
              </div>
            </div>}

          {activeTab === 'attachments' && <div className="space-y-4" data-id="element-277">
              <div data-id="element-278">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-279">
                  Attachments
                </label>
                {document.attachments && document.attachments.length > 0 ? <ul className="space-y-2" data-id="element-280">
                    {document.attachments.map((attachment, index) => <li key={index} className="text-sm text-blue-600 hover:underline cursor-pointer" data-id="element-281">
                        {attachment}
                      </li>)}
                  </ul> : <div className="text-sm text-gray-500" data-id="element-282">No attachments</div>}
              </div>
            </div>}

          {activeTab === 'verification' && <div className="space-y-6" data-id="element-283">
              {/* Verification Status Banner */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3" data-id="element-284">
                <ShieldCheckIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-285" />
                <div data-id="element-286">
                  <div className="text-sm font-semibold text-green-900 mb-1" data-id="element-287">
                    Vendor Verified
                  </div>
                  <div className="text-xs text-green-700" data-id="element-288">
                    Last verified: {details.lastVerifiedDate || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Vendor Information Section */}
              <div data-id="element-289">
                <div className="flex items-center gap-2 mb-3" data-id="element-290">
                  <BuildingIcon className="w-4 h-4 text-gray-600" data-id="element-291" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-292">
                    Vendor Information
                  </h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-293">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3" data-id="element-294">
                    <div data-id="element-295">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-296">
                        Vendor Name:
                      </label>
                      <div className="text-sm font-medium text-gray-900" data-id="element-297">
                        {details.vendorName}
                      </div>
                    </div>
                    <div data-id="element-298">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-299">
                        Supplier Code:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-300">
                        {details.supplierCode}
                      </div>
                    </div>
                    <div data-id="element-301">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-302">
                        Vendor Tax Number:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-303">
                        {details.vendorTaxNo}
                      </div>
                    </div>
                    <div data-id="element-304">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-305">
                        Category:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-306">
                        {details.vendorCategory || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-307">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-308">
                        Registration Date:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-309">
                        {details.registrationDate || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-310">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-311">
                        Payment Terms:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-312">
                        {details.paymentTerms || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div data-id="element-313">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-314">
                  Contact Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-315">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3" data-id="element-316">
                    <div data-id="element-317">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-318">
                        Email:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-319">
                        {details.email}
                      </div>
                    </div>
                    <div data-id="element-320">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-321">
                        Phone Number:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-322">
                        {details.phoneNumber || 'N/A'}
                      </div>
                    </div>
                    <div className="col-span-2" data-id="element-323">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-324">
                        Address:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-325">
                        {details.address}
                        <br data-id="element-326" />
                        {details.city}, {details.state} {details.postcode}
                        <br data-id="element-327" />
                        {details.country}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank Details Section */}
              <div data-id="element-328">
                <div className="flex items-center gap-2 mb-3" data-id="element-329">
                  <CreditCardIcon className="w-4 h-4 text-gray-600" data-id="element-330" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-331">
                    Bank Account Details
                  </h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-332">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3" data-id="element-333">
                    <div data-id="element-334">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-335">
                        Bank Name:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-336">
                        {details.bankName || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-337">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-338">
                        Account Name:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-339">
                        {details.bankAccountName || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-340">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-341">
                        Account Number:
                      </label>
                      <div className="text-sm font-mono text-gray-900" data-id="element-342">
                        {details.bankAccountNumber || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-343">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-344">
                        Routing/BSB:
                      </label>
                      <div className="text-sm font-mono text-gray-900" data-id="element-345">
                        {details.bankBSB || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-346">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-347">
                        SWIFT Code:
                      </label>
                      <div className="text-sm font-mono text-gray-900" data-id="element-348">
                        {details.bankSwiftCode || 'N/A'}
                      </div>
                    </div>
                    <div data-id="element-349">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-350">
                        IBAN:
                      </label>
                      <div className="text-sm font-mono text-gray-900" data-id="element-351">
                        {details.bankIBAN || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verification Check Section */}
              <div data-id="element-352">
                <div className="flex items-center gap-2 mb-3" data-id="element-353">
                  <CalendarIcon className="w-4 h-4 text-gray-600" data-id="element-354" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-355">
                    Verification Status
                  </h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-356">
                  <div className="flex items-center justify-between" data-id="element-357">
                    <div data-id="element-358">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-359">
                        Vendor Bank Check:
                      </label>
                      <div className="flex items-center" data-id="element-360">
                        {details.vendorBankCheck && <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-361" />}
                      </div>
                    </div>
                    <div className="text-right" data-id="element-362">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-363">
                        Last Verified:
                      </label>
                      <div className="text-sm text-gray-900" data-id="element-364">
                        {details.lastVerifiedDate || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ABR Lookup Section - Only show for Australian vendors with ABN */}
              {details.country === 'Australia' && details.vendorTaxNo && details.vendorTaxNo !== 'N/A' && <div data-id="element-365">
                    <div className="flex items-center gap-2 mb-3" data-id="element-366">
                      <SearchIcon className="w-4 h-4 text-gray-600" data-id="element-367" />
                      <h3 className="text-sm font-semibold text-gray-900" data-id="element-368">
                        ABR Lookup (Australian Business Register)
                      </h3>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-369">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3" data-id="element-370">
                        <div data-id="element-371">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-372">
                            ABN Status:
                          </label>
                          <div className="flex items-center gap-2" data-id="element-373">
                            <span className={`text-sm font-medium ${details.abrStatus === 'Active' ? 'text-green-700' : 'text-gray-900'}`} data-id="element-374">
                              {details.abrStatus || 'N/A'}
                            </span>
                            {details.abrStatus === 'Active' && <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-375" />}
                          </div>
                        </div>
                        <div data-id="element-376">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-377">
                            ABN Last Updated Date:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-378">
                            {details.abrLastUpdatedDate || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-379">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-380">
                            ABN Status Effective From:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-381">
                            {details.abrStatusEffectiveFrom || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-382">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-383">
                            ASIC Registration:
                          </label>
                          <div className="text-sm font-mono text-gray-900" data-id="element-384">
                            {details.asicRegistration || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-385">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-386">
                            Entity Name:
                          </label>
                          <div className="text-sm font-medium text-gray-900" data-id="element-387">
                            {details.abrEntityName || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-388">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-389">
                            Entity Type:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-390">
                            {details.abrEntityType || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-391">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-392">
                            GST Effective From:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-393">
                            {details.gstEffectiveFrom || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-394">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-395">
                            Is Deductible Gift Recipient:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-396">
                            {details.isDeductibleGiftRecipient || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-397">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-398">
                            Main Business Location Postcode:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-399">
                            {details.mainBusinessLocationPostcode || 'N/A'}
                          </div>
                        </div>
                        <div data-id="element-400">
                          <label className="block text-xs text-gray-600 mb-1" data-id="element-401">
                            Main Business Location State Code:
                          </label>
                          <div className="text-sm text-gray-900" data-id="element-402">
                            {details.mainBusinessLocationStateCode || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

              {/* Read-Only Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-403">
                <p className="text-xs text-blue-700" data-id="element-404">
                  <strong data-id="element-405">Note:</strong> This information is read-only master
                  data from the vendor management system. To update vendor
                  details, please contact the procurement team.
                </p>
              </div>
            </div>}
        </div>
      </div>

      {/* PO Search Modal */}
      {showPOSearchModal && <POHeaderSearchModal vendorName={document.supplier} onApply={handlePOSelect} onClose={() => setShowPOSearchModal(false)} data-id="element-406" />}
    </>;
}